import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';

import { NotificationService } from '../notifications/notification.service';
import { SUPPRESS_ERROR_TOAST } from './error-context';
import { extractApiErrorMessage, networkErrorMessage } from './error-message';
import { ApiValidationError, parseFieldErrors } from './errors';

/**
 * Centralized HTTP error handling.
 *
 * - 401 → handled exclusively by refresh.interceptor (Phase H Stage 1 / 2).
 *         The refresh interceptor sits closer to the backend in the chain;
 *         it either retries with a new token or fires sessionEvents.expired()
 *         to open the re-login modal. Error handling for 401 used to live
 *         here (redirect to /login on every 401) — replaced by ADR-0046.
 * - 403 → toast "permission denied"; rethrow.
 * - 422 → throw ApiValidationError so feature forms can bind errors to
 *         controls inline.  No toast (forms surface the field errors).
 * - 5xx → toast "server error"; rethrow.
 * - 0 (network) → toast "connection problem"; rethrow.
 */
export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const notifications = inject(NotificationService);
  // Features that render HTTP errors inline set SUPPRESS_ERROR_TOAST on
  // the request context so we don't stack the same message a second time
  // as a global snackbar toast on top of their inline banner.
  const suppressToast = req.context.get(SUPPRESS_ERROR_TOAST);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse) {
        if (err.status === 403) {
          // CSRF failures and object-level permission denials all land at 403.
          // ``err.error.detail`` carries the real reason ("CSRF Failed: CSRF
          // token missing.", "You do not have permission to view this
          // student.") — surface it instead of the generic message so the
          // reviewer can see what actually failed.
          if (!suppressToast) {
            notifications.error(
              extractApiErrorMessage(
                err,
                $localize`:@@errors.forbidden:You do not have permission to perform this action.`,
              ),
            );
          }
        } else if (err.status === 422 || err.status === 400) {
          // Treat both 400 (DRF default) and 422 as validation errors.
          const fieldErrors = parseFieldErrors(err.error);
          return throwError(() => new ApiValidationError(fieldErrors, err.status, err));
        } else if (err.status >= 500) {
          // The global exception handler wraps every unhandled exception in
          // ``{"detail": "An unexpected server error occurred.",
          //   "error_details": "ValidationError: ['This password is too
          //   common.']"}``. Show the ``error_details`` half so operators
          // learn the actual reason (weak password / bad payload / …)
          // instead of a generic "server error" that leaves them stuck.
          if (!suppressToast) {
            notifications.error(
              extractApiErrorMessage(
                err,
                $localize`:@@errors.server_error:Something went wrong on the server. Please try again.`,
              ),
            );
          }
          console.error('[http] server error', err.status, err.url);
        } else if (err.status === 0) {
          if (!suppressToast) {
            notifications.error(networkErrorMessage());
          }
        }
        // 401 falls through to refresh.interceptor — no action here.
      }
      return throwError(() => err);
    }),
  );
};
